import { hasConsent } from '@utils/cookieConsent'

const KLAVIYO_CONSENT_CATEGORY = 'marketing'

export function hasKlaviyoConsent() {
	if (typeof window === 'undefined') return false
	return hasConsent(KLAVIYO_CONSENT_CATEGORY)
}

export function ensureKlaviyoQueue() {
	if (typeof window === 'undefined') return null
	window._learnq = window._learnq || []
	return window._learnq
}

function withKlaviyoConsent(callback) {
	if (typeof window === 'undefined') return
	if (!hasKlaviyoConsent()) return

	callback()
}

export function klaviyoIdentify(identityProperties = {}) {
	if (!identityProperties || typeof identityProperties !== 'object') return
	if (!identityProperties.email) return

	withKlaviyoConsent(() => {
		if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
			window.klaviyo.identify(identityProperties)
			return
		}

		const legacyQueue = ensureKlaviyoQueue()
		if (Array.isArray(legacyQueue)) {
			legacyQueue.push(['identify', identityProperties])
		}
	})
}

export function klaviyoTrack(eventName, properties = {}) {
	if (!eventName) return

	withKlaviyoConsent(() => {
		if (window.klaviyo && typeof window.klaviyo.track === 'function') {
			window.klaviyo.track(eventName, properties || {})
			return
		}

		const legacyQueue = ensureKlaviyoQueue()
		if (Array.isArray(legacyQueue)) {
			legacyQueue.push(['track', eventName, properties || {}])
		}
	})
}

const uniqueStringArray = (values) => {
	if (!Array.isArray(values)) return []
	const uniq = new Set(values.filter(Boolean).map(String))
	return Array.from(uniq)
}

const productUrlFromHandle = (handle) => {
	if (typeof window === 'undefined') return handle ? `/products/${handle}` : undefined
	const origin = window.location?.origin || ''
	return handle ? `${origin}/products/${handle}` : undefined
}

export function buildViewedProductPayload(product) {
	if (!product) return {}

	const categories = Array.isArray(product?.collections)
		? uniqueStringArray(product.collections.map((c) => c?.handle))
		: uniqueStringArray(product?.tags)

	return {
		ProductName: product?.title,
		ProductID: product?.shopifyId || product?.id,
		SKU: undefined,
		Categories: categories,
		ImageURL: product?.featuredImage?.src,
		URL: productUrlFromHandle(product?.handle),
		Brand: 'Blackbough Swim',
		Price: product?.priceRangeV2?.minVariantPrice?.amount,
		CompareAtPrice: undefined,
	}
}

const cartItemsFromLines = (linesEdges = []) =>
	(linesEdges || [])
		.map((edge) => edge?.node)
		.filter(Boolean)
		.map((line) => {
			const variant = line?.merchandise
			const product = variant?.product

			const productCategories = uniqueStringArray(
				[] // prefer collections when available; fall back to product metafield promo if needed later
			)

			return {
				ProductID: variant?.id,
				SKU: undefined,
				ProductName: product?.title,
				Quantity: line?.quantity,
				ItemPrice: variant?.price?.amount ?? line?.cost?.amountPerQuantity?.amount,
				RowTotal: line?.cost?.totalAmount?.amount,
				ProductURL: product?.handle ? productUrlFromHandle(product.handle) : undefined,
				ImageURL: variant?.image?.src || variant?.image?.url || variant?.image?.originalSrc,
				ProductCategories: productCategories,
			}
		})

const cartItemNames = (items = []) => uniqueStringArray(items.map((i) => i?.ProductName))
const cartCategories = (items = []) =>
	uniqueStringArray(items.flatMap((i) => i?.ProductCategories || []))

export function buildAddedToCartPayload({ addedLine, cart } = {}) {
	const lines = cart?.lines?.edges || []
	const items = cartItemsFromLines(lines)

	const addedVariant = addedLine?.merchandise
	const addedProduct = addedVariant?.product

	const addedItemCategories = uniqueStringArray([])

	return {
		$value: Number(cart?.cost?.subtotalAmount?.amount || cart?.cost?.totalAmount?.amount) || undefined,
		AddedItemProductName: addedProduct?.title,
		AddedItemProductID: addedVariant?.id,
		AddedItemSKU: undefined,
		AddedItemCategories: addedItemCategories,
		AddedItemImageURL: addedVariant?.image?.src || addedVariant?.image?.url || addedVariant?.image?.originalSrc,
		AddedItemURL: addedProduct?.handle ? productUrlFromHandle(addedProduct.handle) : undefined,
		AddedItemPrice: addedVariant?.price?.amount ?? addedLine?.cost?.amountPerQuantity?.amount,
		AddedItemQuantity: addedLine?.quantity,
		ItemNames: cartItemNames(items),
		CheckoutURL: cart?.checkoutUrl,
		Items: items,
	}
}

export function buildStartedCheckoutPayload({ cart } = {}) {
	const lines = cart?.lines?.edges || []
	const items = cartItemsFromLines(lines)

	return {
		$event_id: undefined,
		$value: Number(cart?.cost?.subtotalAmount?.amount || cart?.cost?.totalAmount?.amount) || undefined,
		ItemNames: cartItemNames(items),
		CheckoutURL: cart?.checkoutUrl,
		Categories: cartCategories(items),
		Items: items,
	}
}

